import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { PlusIcon as CreateIcon, PenIcon as EditIcon } from 'lucide-react'

import Modal from 'components/Modal'
import { CreateReport2 } from './reports-form-list/create-report_2'
import { CreateReport13 } from './reports-form-list/create-report_13'
import { CreateReportVoucherRedemption } from './reports-form-list/create-voucher-redemption-count-report'
import { CreateReport17 } from './reports-form-list/create-report_17'
import { CreateReport19 } from './reports-form-list/create-report_19'

import { isQSRHClient, isCRClient, isPPClient, isWFClient } from 'setups/controller'
import { projectClient } from 'constants/project'

const REPORTS_OPTIONS = [
  {
    name: 'Merchant Transaction Summary (Report 2)',
    link: '?modal=report_2',
    type: 'report_2',
  },
  {
    name: 'Merchant Transaction (Report 13)',
    link: '?modal=report_13',
    type: 'report_13',
  },
  // only for QSRH, CR, PP UAT
  {
    name: 'Voucher Redemption Count',
    link: '?modal=voucher_redemption',
    type: 'voucher_redemption',
  },
  //only for WF
  {
    name: 'Gift Card Daily Sales (Report 17)',
    link: '?modal=report_17',
    type: 'report_17',
  },
  {
    name: 'Gift Card Sales Summary (Report 19)',
    link: '?modal=report_19',
    type: 'report_19',
  },
] as const

const getModalOptions = () => {
  if (isWFClient(projectClient)) {
    return REPORTS_OPTIONS.filter((option) => option.type !== 'voucher_redemption')
  } else if (isQSRHClient(projectClient) || isCRClient(projectClient) || isPPClient(projectClient)) {
    return REPORTS_OPTIONS.filter((option) => option.type !== 'report_17' && option.type !== 'report_19')
  } else {
    return REPORTS_OPTIONS.filter((option) => option.type !== 'voucher_redemption' && option.type !== 'report_17' && option.type !== 'report_19')
  }
}

type ReportOptionsTypesType =
  | (typeof REPORTS_OPTIONS)[number]['type']
  | undefined

type ReportOptionType = (typeof REPORTS_OPTIONS)[number] | null

const useCampaignListActionsModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams() ?? undefined
  const queryModal =
    (searchParams.get('modal') as ReportOptionsTypesType) ?? undefined
  const [isShowCreateModal, isShowCreateModalSet] = useState(false)
  const [isShowEditModal, isShowEditModalSet] = useState(false)
  const [reportType, reportTypeSet] = useState<ReportOptionType>(null)

  const resetModal = useCallback(() => {
    isShowCreateModalSet(false)
    isShowEditModalSet(false)
    navigate('?modal=null')
  }, [isShowCreateModalSet, isShowEditModalSet, navigate])

  const handleReport = useCallback(
    (queryModal: ReportOptionsTypesType) => {
      const modalOptions = getModalOptions()
      const filtered = modalOptions.filter(
        (item) => item.type === queryModal
      )[0]
      reportTypeSet(filtered)
    },
    [reportTypeSet]
  )

  useEffect(() => {
    if (queryModal === 'report_2') {
      isShowCreateModalSet(true)
      handleReport(queryModal)
    } else if (queryModal === 'report_13') {
      isShowCreateModalSet(true)
      handleReport(queryModal)
    } else if (queryModal === 'report_17') {
      isShowCreateModalSet(true)
      handleReport(queryModal)
    } else if (queryModal === 'report_19') {
      isShowCreateModalSet(true)
      handleReport(queryModal)
    } else if (queryModal === 'voucher_redemption') {
      isShowCreateModalSet(true)
      handleReport(queryModal)
    } else {
      resetModal()
    }
  }, [
    queryModal,
    isShowCreateModalSet,
    isShowEditModalSet,
    resetModal,
    handleReport,
  ])

  return {
    isShowCreateModal,
    isShowEditModal,
    resetModal,
    reportType,
  }
}

export const CreateReportsModal = () => {
  const { reportType, isShowCreateModal, resetModal } =
    useCampaignListActionsModal()

  const modalOptions = getModalOptions()

  return (
    <>
      <div className="flex flex-col gap-4 w-max">
        {modalOptions.map((item) => (
          <button
            key={item.name}
            className="px-3 py-1 border rounded-md border-primary text-primary"
            type="button"
          >
            <Link to={item.link} className="flex items-center gap-2">
              <CreateIcon size={16} />
              <span className="tracking-wider font-light">{item.name}</span>
            </Link>
          </button>
        ))}
      </div>

      {reportType && (
        <Modal
          title={reportType.name}
          open={isShowCreateModal}
          onClose={resetModal}
          noFooter
        >
          {/* REPORT 2 */}
          {reportType.type === 'report_2' && (
            <CreateReport2 onCloseModal={resetModal} />
          )}

          {/* REPORT 13 */}
          {reportType.type === 'report_13' && (
            <CreateReport13 onCloseModal={resetModal} />
          )}

          {/* REPORT Voucher Redemption */}
          {reportType.type === 'voucher_redemption' && (
            <CreateReportVoucherRedemption onCloseModal={resetModal} />
          )}

          {/* REPORT 17 */}
          {reportType.type === 'report_17' && (
            <CreateReport17 onCloseModal={resetModal} />
          )}

          {/* REPORT 19 */}
          {reportType.type === 'report_19' && (
            <CreateReport19 onCloseModal={resetModal} />
          )}
        </Modal>
      )}
    </>
  )
}
